// Pure predicate for "this issue asks a yes/no question whose NO answer is
// 'this person is on their own' " — the gap Donna hit on 2026-07-30.
//
// spouse-pass asks "Does <Name> belong in a partner's household?" and the
// decide rail offered exactly one button, the YES path (search for the
// partner's household and merge). The NO answer existed only as the generic
// duplicate-family escape hatch labelled "Different people — keep separate",
// sitting below the fold under "Or do something else" — wrong words for a
// single-member household (there is only one person in the issue, so there
// are no "different people" to keep separate) and not visibly an answer to
// the question. Donna's report was exact: "the answer is no but there is
// nothing to click to answer this."
//
// Deliberately an explicit slug allowlist, not a shape heuristic — the
// other detectors routed through the same search-merge button have
// different NO answers. hh-quarantined's is "this household isn't real",
// trust-reattach's is "leave the trust where it is"; neither is answered by
// "they live alone". Add a slug here only after confirming that sentence is
// literally the right answer for it.
//
// Split into its own module (same convention as
// household-search-merge-predicate.ts) so it is unit-testable from
// tests/web/ without dragging web/src/api.ts (DOM-typed) into the root
// tsconfig program.

// `| undefined` is explicit because the repo runs with
// exactOptionalPropertyTypes: callers legitimately hold an issue whose
// detectorSlug is undefined, and the predicate's whole job is to answer
// "not one of mine" for it.
export interface LivesAloneIssueLike {
  detectorSlug?: string | undefined;
  affectedRecords?: { kind: string; id: string; label: string }[] | undefined;
}

export interface LivesAloneAnswer {
  /** Button copy — reads as the answer to the question, not as a verb. */
  label: string;
  /** Auto-filled rationale, so answering No stays one click (UX rule 8). */
  rationale: string;
  /** Plain-English consequence shown under the button (UX rule 3). */
  consequence: string;
}

const SLUGS = new Set(["spouse-pass", "hh-one-contact"]);

export function livesAloneAnswer(
  issue: LivesAloneIssueLike,
): LivesAloneAnswer | null {
  if (!issue.detectorSlug || !SLUGS.has(issue.detectorSlug)) return null;
  // spouse-pass's phone-matched shape (2026-07-30) names a candidate
  // partner household — two household refs. Its question is "are these
  // two households one couple?", and its No is the duplicate-family
  // "Different people — keep separate", not "they live alone".
  const householdRefs = (issue.affectedRecords ?? []).filter(
    (r) => r.kind === "household",
  );
  if (householdRefs.length > 1) return null;
  const person =
    (issue.affectedRecords ?? []).find((r) => r.kind === "contact")?.label ??
    null;
  return {
    label: person
      ? `No — ${person} is on their own`
      : "No — this person is on their own",
    rationale: "Lives alone — this household is correct as it stands.",
    // Says what happens to the RECORD, not what happens to the issue row:
    // the steward's worry answering No is "am I deleting something?".
    consequence:
      "Nothing on the record changes. This household stops being flagged and won't be asked again.",
  };
}
